import FhirSearchService from '../services/FhirSearchService';
import useService from './useService';
import { projectCohortPickerSelectors } from '../redux/modules/projectCohortPicker';
import { selectActiveProjectId } from '../redux/modules/projectPicker/selectors';
import { useSelector } from 'react-redux';
import { useInfiniteQuery } from '@tanstack/react-query';

const PAGE_SIZE = 25;

const fhirSearchService = new FhirSearchService();
type PaginationToken = {
  type: 'patient-list-chunking';
  chunkIndex: number;
} | null;

/**
 * This chunks out the source subject list into independent fss requests
 */
const filteredSubjectLoad = async (
  fhirSearch: any, // TODO: Type this
  projectId: string | null,
  subjectIds: Set<string>,
  paginationToken: PaginationToken,
  cohortId: string | null,
) => {
  const safeChunkIndex =
    paginationToken?.type === 'patient-list-chunking' &&
    paginationToken.chunkIndex
      ? paginationToken.chunkIndex
      : 0;
  const safeIdSetArray = [...(subjectIds || [])];
  let idList = safeIdSetArray.slice(0, PAGE_SIZE);
  if (safeChunkIndex) {
    idList = safeIdSetArray.slice(
      safeChunkIndex * PAGE_SIZE,
      (safeChunkIndex + 1) * PAGE_SIZE,
    );
  }

  const response = await fhirSearch(
    projectId,
    {
      type: 'select',
      limit: [
        {
          type: 'number',
          value: 0,
        },
        {
          type: 'number',
          value: PAGE_SIZE,
        },
      ],
      columns: '*',
      from: [
        {
          table: 'patient',
        },
      ],
      where: {
        type: 'elasticsearch',
        query: {
          terms: {
            'id.keyword': idList.map(_ => _.split('/').pop()),
          },
        },
      },
    },
    null,
    {},
    cohortId,
  );

  const patientList = response?.data?.hits?.hits?.map?.(
    (_: { _source: any }) => _._source,
  );

  const nextPaginationToken: PaginationToken =
    (safeChunkIndex + 1) * PAGE_SIZE >= subjectIds.size
      ? null
      : { type: 'patient-list-chunking', chunkIndex: safeChunkIndex + 1 };

  return {
    patientList,
    paginationToken: nextPaginationToken,
  };
};

/**
 * Given a list of subject IDs, this will query for the fhir patient resources with the set of IDs
 */
export default function useSubjectsList(
  subjectIds: Set<string>,
  placeholderData: any = {
    pages: [{ patientList: [] }],
    pageParams: [],
  },
) {
  const [fhirSearch] = useService(fhirSearchService.search);
  const activeCohortId = useSelector(
    projectCohortPickerSelectors.selectActiveProjectCohortId,
  );
  const activeProjectId = useSelector(selectActiveProjectId);

  return useInfiniteQuery<{
    patientList: any[];
    paginationToken?: PaginationToken;
  }>(
    ['subjectsList', subjectIds],
    async ({ pageParam }) =>
      await filteredSubjectLoad(
        fhirSearch,
        activeProjectId,
        subjectIds,
        pageParam,
        activeCohortId,
      ),
    {
      getNextPageParam: lastPage => lastPage.paginationToken,
      getPreviousPageParam: () => undefined,
      enabled: subjectIds.size > 0,
      placeholderData,
    },
  );
}
